import React, { useState, useEffect } from 'react';
import styles from './ChangeOwnerPopup.module.css';
import { UserDisplay } from '../../../Types/User';
import LoadingIcon from '../LoadingIcon/LoadingIcon';
import { ChangeProductAreaOwner } from '../../../API/Improve/Improve';
import { axiosInstance } from '../../../Authentication/AxiosInterceptor';
import { ClientUserDisplayInfo } from '../../../Types/ClientUserDisplayInfo';

type ChangeOwnerPopupProps = {
  areaId: string;
  availableUsers?: UserDisplay[];
  canChangeOwner?: boolean;
  onOwnerChanged: () => void;
  onClose: () => void;
};

const ChangeOwnerPopup: React.FC<ChangeOwnerPopupProps> = ({
  areaId,
  availableUsers,
  canChangeOwner = true,
  onOwnerChanged,
  onClose
}) => {
  const [selectedUserSub, setSelectedUserSub] = useState<string>('');
  const [isChanging, setIsChanging] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentOwnerSub, setCurrentOwnerSub] = useState<string | undefined>();
  const [loadingOwner, setLoadingOwner] = useState<boolean>(true);
  const [usersList, setUsersList] = useState<ClientUserDisplayInfo[]>([]);

  useEffect(() => {
    const fetchUsersAndOwner = async () => {
      try {
        setLoadingOwner(true);
        const response = await axiosInstance.get<ClientUserDisplayInfo[]>(
          `GetAreaUserDisplayInfos/${areaId}`
        );
        setUsersList(response.data);
        
        // Find the user with "Owner" role
        const owner = response.data.find(user => user.roles?.includes('Owner'));
        if (owner) {
          setCurrentOwnerSub(owner.sub);
        }
      } catch (error) {
        console.error('Error fetching users and owner:', error);
        setError('Failed to load users. Please try again.');
      } finally {
        setLoadingOwner(false);
      }
    };

    if (areaId) {
      fetchUsersAndOwner();
    }
  }, [areaId]);

  const handleUserSelect = (userSub: string) => {
    setSelectedUserSub(userSub);
    setError(null);
  };

  const handleChangeOwner = async () => {
    if (!canChangeOwner) {
      setError('You do not have permission to change the owner.');
      return;
    }

    if (!selectedUserSub) {
      setError('Please select a user');
      return;
    }

    if (selectedUserSub === currentOwnerSub) {
      setError('This user is already the owner');
      return;
    }

    try {
      setIsChanging(true);
      setError(null);
      await ChangeProductAreaOwner(areaId, selectedUserSub);
      onOwnerChanged();
      onClose();
    } catch (err: any) {
      console.error('Error changing owner:', err);
      setError(err.response?.data?.message || 'Failed to change owner. Please try again.');
    } finally {
      setIsChanging(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.viewUsersContainer}>
          <h3>Select New Owner</h3>
          <div className={styles.usersList}>
            {loadingOwner ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
                <LoadingIcon />
              </div>
            ) : usersList.length > 0 ? (
              usersList.map((user) => {
                const isSelected = selectedUserSub === user.sub;
                const isCurrentOwner = currentOwnerSub === user.sub;
                
                return (
                  <div
                    key={user.sub}
                    className={`${styles.userItem} ${isSelected ? styles.selected : ''} ${isCurrentOwner ? styles.currentOwner : ''}`}
                    onClick={() => !isCurrentOwner && handleUserSelect(user.sub)}
                  >
                    <div className={styles.userInfo}>
                      <div className={styles.userName}>{user.fullName}</div>
                      <div className={styles.userEmail}>{user.email}</div>
                    </div>
                    <div className={styles.userRole}>
                      {user.roles && user.roles.length > 0 ? (
                        user.roles.map((role, index) => (
                          <span key={index} className={styles.roleBadge}>
                            {role}
                          </span>
                        ))
                      ) : (
                        <span className={styles.noRole}>No role assigned</span>
                      )}
                      {isCurrentOwner && (
                        <span className={styles.currentOwnerBadge}>Current Owner</span>
                      )}
                      {isSelected && !isCurrentOwner && (
                        <span className={styles.selectedBadge}>Selected</span>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className={styles.noUsers}>No users found in this area</div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className={styles.errorContainer}>
          <div className={styles.error}>{error}</div>
        </div>
      )}

      <div className={styles.actions}>
        <button
          className={styles.cancelButton}
          onClick={onClose}
          disabled={isChanging}
        >
          Cancel
        </button>
        <button
          className={styles.confirmButton}
          onClick={handleChangeOwner}
          disabled={isChanging || !selectedUserSub || selectedUserSub === currentOwnerSub || !canChangeOwner}
        >
          {isChanging ? 'Changing...' : 'Change Owner'}
        </button>
      </div>
    </div>
  );
};

export default ChangeOwnerPopup;

